import { useApi } from '@backstage/core-plugin-api';
import { visitsApiRef, type Visit } from '@backstage/plugin-home';
import { useEffect, useState } from 'react';

export interface RecentVisits {
  loading: boolean;
  visits: Visit[];
}

// Collapse a newest-first visit list to at most `limit` entries, one per entity.
// Keying on entityRef (falling back to pathname) removes duplicates left by
// records stored per entity sub-tab before pathname normalization landed.
// Keeping the first occurrence keeps the most recent visit of each entity.
export function dedupeByEntity(visits: Visit[], limit: number): Visit[] {
  if (limit <= 0) return [];
  const seen = new Set<string>();
  const result: Visit[] = [];
  for (const visit of visits) {
    const key = visit.entityRef || visit.pathname;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(visit);
    if (result.length >= limit) break;
  }
  return result;
}

// The signed-in user's most recent entity visits, newest first. Read by the home
// page, which both renders them and uses "no visits and nothing starred" as the
// first-run signal for the empty state (mock 1d).
export function useRecentVisits(limit: number): RecentVisits {
  const visitsApi = useApi(visitsApiRef);
  const [state, setState] = useState<RecentVisits>({
    loading: true,
    visits: [],
  });

  useEffect(() => {
    let cancelled = false;
    // Over-fetch, then dedupe by entity so a single entity never shows more
    // than once — covers records already stored per sub-tab before pathname
    // normalization landed.
    visitsApi
      .list({ limit: limit * 5, orderBy: [{ field: 'timestamp', direction: 'desc' }] })
      .then(all => {
        if (!cancelled)
          setState({ loading: false, visits: dedupeByEntity(all, limit) });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, visits: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [visitsApi, limit]);

  return state;
}
